import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ChevronDown, FileText, HandCoins, RefreshCw, XCircle } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const resolveReimburseSourceLabel = (entry) => {
  const sourceTransactionNumbers = Array.isArray(entry?.reimburse_source_transaction_numbers)
    ? entry.reimburse_source_transaction_numbers.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (sourceTransactionNumbers.length > 0) return sourceTransactionNumbers.join(', ');
  return String(entry?.reimburse_source_transaction_number || '').trim() || '-';
};

const resolveFileUrl = (path) => {
  const value = String(path || '').trim();
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${BACKEND_URL}${value}`;
};

const getApprovalBadgeClass = (status) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'APPROVED') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
  if (normalized === 'REJECTED') return 'border-red-500/30 bg-red-500/10 text-red-300';
  return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
};

const getApprovalCardClass = (status) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'APPROVED') return 'border-l-emerald-400/70';
  if (normalized === 'REJECTED') return 'border-l-red-400/70';
  return 'border-l-[#D4AF37]';
};

function SummaryCard({ label, value, helper, tone = 'neutral' }) {
  const toneClass = {
    pending: 'border-amber-500/25 bg-amber-500/10 text-amber-200',
    info: 'border-sky-500/20 bg-sky-500/10 text-sky-200',
    approved: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-200',
    rejected: 'border-red-500/20 bg-red-500/10 text-red-200',
    neutral: 'border-white/10 bg-white/[0.02] text-white',
  }[tone] || 'border-white/10 bg-white/[0.02] text-white';

  return (
    <div className={`rounded-sm border p-4 ${toneClass}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-75">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
      {helper ? <p className="mt-1 text-xs opacity-70">{helper}</p> : null}
    </div>
  );
}

export default function ApproveReimburse() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [approvingEntryId, setApprovingEntryId] = useState('');
  const [rejectingEntryId, setRejectingEntryId] = useState('');
  const [expandedEntryId, setExpandedEntryId] = useState('');

  const fetchReimburseRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/petty-cash`, { withCredentials: true });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setSummary(data?.summary || {});
    } catch (error) {
      console.error('Failed to fetch reimburse requests:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil data pengajuan reimburse.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReimburseRequests();
  }, []);

  const reimburseEntries = useMemo(
    () =>
      entries.filter(
        (entry) => String(entry.entry_type || '').toUpperCase() === 'REIMBURSE'
      ),
    [entries]
  );

  const pendingReimburseEntries = useMemo(
    () => reimburseEntries.filter((entry) => String(entry.approval_status || '').toUpperCase() === 'PENDING'),
    [reimburseEntries]
  );
  const approvedReimburseEntries = useMemo(
    () => reimburseEntries.filter((entry) => String(entry.approval_status || '').toUpperCase() === 'APPROVED'),
    [reimburseEntries]
  );
  const rejectedReimburseEntries = useMemo(
    () => reimburseEntries.filter((entry) => String(entry.approval_status || '').toUpperCase() === 'REJECTED'),
    [reimburseEntries]
  );

  const pendingTotal = useMemo(
    () => pendingReimburseEntries.reduce((total, entry) => total + Number(entry.amount || 0), 0),
    [pendingReimburseEntries]
  );

  const approveReimburse = async (entry) => {
    const entryId = String(entry?.id || '').trim();
    if (!entryId) return;
    const ok = window.confirm(`Approve reimburse ${entry?.transaction_number || entryId}?`);
    if (!ok) return;

    try {
      setApprovingEntryId(entryId);
      await axios.post(`${API}/petty-cash/entries/${entryId}/approve-reimburse`, {}, { withCredentials: true });
      await fetchReimburseRequests();
    } catch (error) {
      console.error('Failed to approve reimburse:', error);
      alert(error.response?.data?.detail || 'Gagal approve reimburse.');
    } finally {
      setApprovingEntryId('');
    }
  };

  const rejectReimburse = async (entry) => {
    const entryId = String(entry?.id || '').trim();
    if (!entryId) return;
    const ok = window.confirm(`Reject reimburse ${entry?.transaction_number || entryId}?`);
    if (!ok) return;

    try {
      setRejectingEntryId(entryId);
      await axios.post(`${API}/petty-cash/entries/${entryId}/reject-reimburse`, {}, { withCredentials: true });
      await fetchReimburseRequests();
    } catch (error) {
      console.error('Failed to reject reimburse:', error);
      alert(error.response?.data?.detail || 'Gagal reject reimburse.');
    } finally {
      setRejectingEntryId('');
    }
  };

  return (
    <DashboardLayout>
      <Header title="Approve Reimburse" />
      <div className="space-y-5 p-6 lg:p-8" data-testid="approve-reimburse-page">
        <div className="flex flex-col gap-4 rounded-sm border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),rgba(9,9,11,0)_42%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              Admin Approval
            </Badge>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Approve Reimburse</h2>
            <p className="mt-1 text-sm text-zinc-400">
              Pengajuan reimburse dari kasir akan masuk ke sini sebelum Petty Cash Out ditandai sudah direimburse.
            </p>
          </div>
          <Button
            type="button"
            onClick={fetchReimburseRequests}
            className="rounded-sm border border-white/20 bg-white/[0.03] text-white hover:bg-white/[0.08]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <SummaryCard label="Pending" value={pendingReimburseEntries.length} helper="Menunggu admin" tone="pending" />
          <SummaryCard label="Nominal Pending" value={formatCurrency(pendingTotal)} helper="Belum disetujui" tone="info" />
          <SummaryCard label="Approved" value={approvedReimburseEntries.length} helper={formatCurrency(summary.total_reimburse || 0)} tone="approved" />
          <SummaryCard label="Rejected" value={rejectedReimburseEntries.length} helper="Ditolak admin" tone="rejected" />
          <SummaryCard label="Riwayat" value={reimburseEntries.length} helper="Semua pengajuan" tone="neutral" />
        </div>

        <div className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="flex flex-col gap-1 border-b border-white/10 bg-white/[0.02] px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Riwayat Pengajuan Reimburse</p>
              <p className="mt-1 text-xs text-zinc-600">Klik nomor petty cash untuk menampilkan detail transaksi sumber reimburse.</p>
            </div>
            <p className="text-xs text-zinc-500">{reimburseEntries.length} pengajuan</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : reimburseEntries.length > 0 ? (
            <div className="max-h-[620px] space-y-3 overflow-auto p-4">
              {reimburseEntries.map((entry) => {
                const status = String(entry.approval_status || 'APPROVED').toUpperCase();
                const sourceEntries = Array.isArray(entry.reimburse_source_entries) ? entry.reimburse_source_entries : [];
                const isPending = status === 'PENDING';
                const entryId = String(entry.id || '');
                const isExpanded = expandedEntryId === entryId;
                return (
                <div key={entry.id} className={`overflow-hidden rounded-sm border border-l-4 border-white/10 bg-black/20 text-sm shadow-sm shadow-black/10 ${getApprovalCardClass(status)}`}>
                  <div className="grid gap-4 border-b border-white/10 bg-white/[0.025] px-4 py-4 lg:grid-cols-[minmax(220px,1.2fr)_minmax(150px,.8fr)_minmax(130px,.7fr)_minmax(180px,1fr)_auto] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setExpandedEntryId((prev) => (prev === entryId ? '' : entryId))}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-sm px-1 py-0.5 text-left font-mono text-[13px] font-semibold text-zinc-100 transition-colors hover:bg-white/[0.06] hover:text-[#F3D77A]"
                          aria-expanded={isExpanded}
                        >
                          <span className="truncate">{entry.transaction_number || '-'}</span>
                          <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        <span className={`inline-flex rounded-sm border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${getApprovalBadgeClass(status)}`}>
                          {status}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-zinc-600">Diajukan {formatDateTime(entry.created_at)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">Sumber Out</p>
                      <p className="mt-1 truncate text-xs font-semibold text-sky-300">{resolveReimburseSourceLabel(entry)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">Nominal</p>
                      <p className="mt-1 font-semibold text-red-300 tabular-nums">-{formatCurrency(entry.amount)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">Pengaju</p>
                      <p className="mt-1 truncate text-xs font-semibold text-zinc-200">{entry.created_by || '-'}</p>
                      {entry.merchant_name ? <p className="mt-0.5 truncate text-xs text-zinc-500">{entry.merchant_name}</p> : null}
                      <p className="mt-0.5 text-xs text-zinc-500">
                        {status === 'APPROVED' ? `Approved: ${formatDateTime(entry.approved_at)}` : ''}
                        {status === 'REJECTED' ? `Rejected: ${formatDateTime(entry.rejected_at)}` : ''}
                        {status === 'PENDING' ? 'Belum diproses' : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {isPending ? (
                        <>
                          <Button
                            type="button"
                            onClick={() => approveReimburse(entry)}
                            disabled={approvingEntryId === entry.id || rejectingEntryId === entry.id}
                            className="h-9 rounded-sm bg-emerald-500/15 px-3 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <CheckCircle2 className="mr-1.5 h-4 w-4" />
                            {approvingEntryId === entry.id ? 'Approve...' : 'Approve'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => rejectReimburse(entry)}
                            disabled={approvingEntryId === entry.id || rejectingEntryId === entry.id}
                            className="h-9 rounded-sm bg-red-500/15 px-3 text-xs font-semibold text-red-300 ring-1 ring-red-500/30 hover:bg-red-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <XCircle className="mr-1.5 h-4 w-4" />
                            {rejectingEntryId === entry.id ? 'Reject...' : 'Reject'}
                          </Button>
                        </>
                      ) : (
                        <span className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-zinc-500">Riwayat</span>
                      )}
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="px-4 py-3">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-600">Detail Petty Cash Out</p>
                        <p className="text-xs text-zinc-600">{sourceEntries.length} transaksi</p>
                      </div>
                      {sourceEntries.length > 0 ? (
                        <div className="overflow-hidden rounded-sm border border-white/10">
                          <div className="hidden grid-cols-[1fr_130px_150px_90px] gap-3 border-b border-white/10 bg-white/[0.02] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600 md:grid">
                            <span>Transaksi</span>
                            <span>Nominal</span>
                            <span>Tanggal</span>
                            <span>Bukti</span>
                          </div>
                          <div className="divide-y divide-white/5">
                            {sourceEntries.map((source) => (
                              <div key={source.id} className="grid gap-3 px-3 py-3 md:grid-cols-[1fr_130px_150px_90px] md:items-center">
                                <div className="min-w-0">
                                  <p className="truncate font-mono text-xs font-semibold text-zinc-200">{source.transaction_number || '-'}</p>
                                  <p className="mt-1 truncate text-xs">
                                    <span className="text-[#F3D77A]">{source.category || '-'}</span>
                                    <span className="text-zinc-600"> - </span>
                                    <span className="text-zinc-400">{source.activity || '-'}</span>
                                  </p>
                                  {source.notes ? <p className="mt-1 truncate text-xs text-zinc-600">{source.notes}</p> : null}
                                </div>
                                <p className="text-sm font-semibold text-red-300">-{formatCurrency(source.amount)}</p>
                                <p className="text-xs text-zinc-500">{formatDateTime(source.created_at || source.entry_date)}</p>
                              {source.photo_path ? (
                                <a
                                  href={resolveFileUrl(source.photo_path)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs font-semibold text-sky-300 hover:text-sky-200"
                                >
                                  Lihat Bukti
                                </a>
                              ) : (
                                <span className="text-xs text-zinc-600">Tidak ada bukti</span>
                              )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-sm border border-white/10 px-3 py-4 text-xs text-zinc-500">Detail sumber reimburse tidak ditemukan.</div>
                      )}
                    </div>
                  ) : null}
                </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <HandCoins className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
              <p className="text-sm font-medium text-white">Belum ada pengajuan reimburse</p>
              <p className="mt-1 text-sm text-zinc-500">Kalau kasir mengajukan reimburse, riwayatnya akan muncul di sini.</p>
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 rounded-sm border border-white/10 bg-white/[0.02] p-4 text-sm text-zinc-400">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
          <p>
            Pengajuan yang sudah approve atau reject tetap tersimpan di halaman ini sebagai riwayat admin.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

